import { useEffect, useRef, useState } from "react";

const useScrollDetect = (
  element: Element,
  onScroll: (amount: number) => void,
  onStart: () => void,
  onEnd: () => void,
) => {
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    if (!element) return;
    const handleScroll = () => {
      const scrollTop = element.scrollTop;
      const lastScrollTop = lastScrollTopRef.current;

      if (onScroll) onScroll(scrollTop - lastScrollTop);
      if (onStart && scrollTop === 0) onStart();
      else if (
        onEnd &&
        scrollTop + 20 >= element.scrollHeight - element.clientHeight
      ) {
        onEnd();
      }
      lastScrollTopRef.current = scrollTop;
    };

    element.addEventListener("scroll", handleScroll);

    // Detach the event listener when the component unmounts
    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, [element, onScroll, onStart, onEnd]);

  return lastScrollTopRef;
};

export const useAutoScroll = (containerObj: Element) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const lastScrollTopRef = useRef(0);

  const autoScrollOn = () => setAutoScroll(true);
  const autoScrollOff = () => setAutoScroll(false);

  useEffect(() => {
    if (!containerObj) return;

    const handleContentChange = () => {
      if (autoScroll && containerObj.scrollHeight > lastScrollTopRef.current) {
        lastScrollTopRef.current = containerObj.scrollHeight;
        scrollToBottom(containerObj);
      }
    };

    const mutationObserver = new MutationObserver(handleContentChange);
    mutationObserver.observe(containerObj, { childList: true, subtree: true });

    return () => mutationObserver.disconnect();
  }, [containerObj, autoScroll]);

  return [autoScrollOn, autoScrollOff];
};

export const scrollToBottom = (containerObj: any) => {
  containerObj.scrollTo({
    top: containerObj.scrollHeight,
    behavior: "smooth",
  });
};

export const scrollToObject = (containerObj: any, obj: any) => {
  if (containerObj) {
    containerObj.scrollTo({
      top: obj.offsetTop - 20,
      behavior: "smooth",
    });
  }
};

export default useScrollDetect;
